import React, {
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Field, FieldPropsExt, MaskedInputText } from '../../components';
import { MaskGenerator } from 'react-hook-mask';

export interface MaskedTextFieldProps extends FieldPropsExt {
  placeholder?: string;
  type?: 'text' | 'password';
  value: string | null;
  validate?: boolean;
  maskGenerator: MaskGenerator;
  onChange: (value: string) => void;
  validationMethod?: (value: string) => string;
}

const MaskedTextFieldComponent: React.ForwardRefRenderFunction<
  HTMLInputElement | null,
  MaskedTextFieldProps
> = (props: MaskedTextFieldProps, ref: Ref<HTMLInputElement | null>) => {
  const { type = 'text', validate: propsValidate = false } = props;
  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const [validate, setValidate] = useState<boolean>(propsValidate ?? false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    props.errorMessage
  );
  const [value, setValue] = useState<string>(
    props.value === null ? '' : props.value
  );

  const inputRef = useRef<HTMLInputElement>(null);

  const onFocus = () => {
    setHasFocus(true);
  };

  const onFieldFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // destructured to make lint happy
  const { required, errorMessage: errMsg, label, validationMethod } = props;
  const checkIfRequired = useCallback(() => {
    if (required && value.length === 0) {
      setErrorMessage(`${label} is required`);
    } else {
      const validationString = validationMethod ? validationMethod(value) : '';
      if (validationString) {
        setErrorMessage(validationString);
      } else {
        setErrorMessage(errMsg);
      }
    }
  }, [required, value, errMsg, label, validationMethod]);

  const onBlur = () => {
    setHasFocus(false);
    setValidate(true);
    checkIfRequired();

    if (props.onLostFocus) {
      props.onLostFocus();
    }
  };

  useEffect(() => {
    if (validate) {
      checkIfRequired();
    } else {
      setErrorMessage(props.errorMessage);
    }
  }, [checkIfRequired, validate, props.errorMessage]);

  useEffect(() => {
    setValue(props.value === null ? '' : props.value);
  }, [props.value]);

  useEffect(() => {
    setValidate(propsValidate ?? false);
  }, [propsValidate]);

  useImperativeHandle(ref, () => inputRef?.current);

  return (
    <Field
      {...props}
      errorMessage={errorMessage}
      hasFocus={hasFocus}
      onFocus={onFieldFocus}
    >
      <MaskedInputText
        ref={inputRef}
        value={props.value ?? ''}
        type={type ?? 'text'}
        inputMode="text"
        disabled={props.disabled || props.validating}
        placeholder={props.placeholder}
        maskGenerator={props.maskGenerator}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={props.onChange}
      />
    </Field>
  );
};

const MaskedTextField = forwardRef(MaskedTextFieldComponent);

export default MaskedTextField;
